/**
 * Optimized Image Loader
 * Progressive image loading with Low Quality Image Placeholders (LQIP)
 * Prevents Cumulative Layout Shift (CLS) and improves perceived performance
 * 
 * Performance benefits:
 * - Instant placeholder display (no white boxes)
 * - Progressive enhancement (blur-up effect)
 * - Automatic aspect ratio preservation
 * - Bandwidth-conscious loading strategies
 */

class OptimizedImageLoader {
  constructor() {
    this.observer = null;
    this.loadedImages = new WeakSet();
    this.imageQueue = [];
    this.isProcessing = false;
    this.BATCH_SIZE = 4; // Load 4 images simultaneously
    this.BATCH_DELAY = 50; // 50ms between batches
  }

  /**
   * Initialize the image loader
   */
  init() {
    if (this.observer) return;

    // Detect connection speed for adaptive loading
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const isSlow = connection && (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g');
    
    // Adjust strategy based on connection
    const rootMargin = isSlow ? '50px' : '200px';
    
    // Create IntersectionObserver for lazy loading
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            this.queueImageLoad(img);
            this.observer.unobserve(img); // Load once
          }
        });
      },
      {
        root: null,
        rootMargin: rootMargin,
        threshold: 0.01
      }
    );

    // Find and observe all images
    this.observeImages();
    
    // Preload critical images (hero, above-the-fold)
    this.preloadCriticalImages();
  }

  /**
   * Preload critical above-the-fold images immediately
   */
  preloadCriticalImages() {
    // Load hero section images with high priority
    const heroImages = document.querySelectorAll('[data-priority="high"] img, .hero img, [data-critical-image]');
    
    heroImages.forEach((img, index) => {
      // Load first 3 images immediately, rest with small delay
      const delay = index < 3 ? 0 : index * 100;
      setTimeout(() => {
        this.loadImage(img, true);
      }, delay);
    });
  }

  /**
   * Find and observe all lazy-loadable images
   */
  observeImages() {
    // Select images that need lazy loading
    const images = document.querySelectorAll('img[loading="lazy"], img[data-src]');
    
    images.forEach(img => {
      // Skip if already loaded or observed
      if (this.loadedImages.has(img) || img.complete) {
        return;
      }
      
      // Add aspect ratio preservation if not set
      if (!img.style.aspectRatio && img.dataset.aspectRatio) {
        img.style.aspectRatio = img.dataset.aspectRatio;
      }
      
      // Add LQIP background if available
      if (img.dataset.placeholder) {
        img.style.backgroundImage = `url('${img.dataset.placeholder}')`;
        img.style.backgroundSize = 'cover';
        img.style.backgroundPosition = 'center';
      } else {
        // Use solid color placeholder
        img.style.backgroundColor = '#f0f0f0';
      }
      
      this.observer.observe(img);
    });
  }

  /**
   * Add image to load queue
   * @param {HTMLImageElement} img - Image to queue
   */
  queueImageLoad(img) {
    if (this.loadedImages.has(img)) return;
    
    this.imageQueue.push(img);
    this.processQueue();
  }

  /**
   * Process image load queue in batches
   */
  processQueue() {
    if (this.isProcessing || this.imageQueue.length === 0) return;
    
    this.isProcessing = true;
    
    // Get next batch
    const batch = this.imageQueue.splice(0, this.BATCH_SIZE);
    
    // Load batch
    const loadPromises = batch.map(img => this.loadImage(img));
    
    // Wait for batch to complete before processing next
    Promise.all(loadPromises).finally(() => {
      this.isProcessing = false;
      
      // Process next batch if queue has items
      if (this.imageQueue.length > 0) {
        setTimeout(() => this.processQueue(), this.BATCH_DELAY);
      }
    });
  }

  /**
   * Load a single image with progressive enhancement
   * @param {HTMLImageElement} img - Image to load
   * @param {boolean} priority - High priority (skip queue)
   * @returns {Promise}
   */
  loadImage(img, priority = false) {
    return new Promise((resolve) => {
      // Skip if already loaded
      if (this.loadedImages.has(img)) {
        resolve();
        return;
      }
      
      // Mark as loading
      img.classList.add('image-loading');
      
      // Get image source
      const src = img.dataset.src || img.src;
      if (!src) {
        resolve();
        return;
      }
      
      // Create temporary image for loading
      const tempImg = new Image();
      
      // Success handler
      tempImg.onload = () => {
        // Update source
        if (img.dataset.src) {
          img.src = img.dataset.src;
          delete img.dataset.src;
        }
        
        // Update srcset if available
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          delete img.dataset.srcset;
        }
        
        // Add loaded class for fade-in effect
        img.classList.remove('image-loading');
        img.classList.add('image-loaded');
        
        // Remove placeholder background
        setTimeout(() => {
          img.style.backgroundImage = '';
          img.style.backgroundColor = '';
        }, 300); // Match CSS transition duration
        
        // Mark as loaded
        this.loadedImages.add(img);
        
        resolve();
      };
      
      // Error handler
      tempImg.onerror = () => {
        console.warn('Failed to load image:', src);
        img.classList.remove('image-loading');
        img.classList.add('image-error');
        resolve(); // Resolve anyway to continue queue
      };
      
      // Set priority
      if (priority) {
        tempImg.fetchPriority = 'high';
      }
      
      // Start loading
      tempImg.src = src;
      
      // If image already cached, trigger onload immediately
      if (tempImg.complete) {
        tempImg.onload();
      }
    });
  }

  /**
   * Refresh observer (for dynamically added images)
   */
  refresh() {
    this.observeImages();
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.imageQueue = [];
    this.loadedImages = new WeakSet();
  }
}

// Create singleton
window.optimizedImageLoader = window.optimizedImageLoader || new OptimizedImageLoader();

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.optimizedImageLoader.init();
  });
} else {
  window.optimizedImageLoader.init();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = OptimizedImageLoader;
}

